import React, { useEffect } from "react";
import {
  SecondaryGrid,
  GridColumn,
  useGrid,
  useFlyoutContainer,
  GridRecordClickEventArgs,
  flyoutCloseEventAtom,
} from "@ucl/library";
import { Predicate } from "@syncfusion/ej2-data";
import variables from "@/config/variables";
import { CreditAuthorizationODataDTO } from "@/types/CreditAuthorizationODataDTO";
import "./CreditAuthorizationsGrid.scss";
import { useAtom } from "jotai";

interface CreditAuthorizationsGridProps {
  parentEntityId?: string;
  parentEntityType?: string;
  getAuthRequestHeader: () => Promise<Record<string, string>>;
  financialApplicationId?: string;
  isApplicantLinked?: boolean;
}

export const CreditAuthorizationsGrid: React.FC<CreditAuthorizationsGridProps> = ({
  parentEntityId,
  parentEntityType,
  getAuthRequestHeader,
  financialApplicationId,
  isApplicantLinked = false,
}) => {
  const { navigateToFlyout } = useFlyoutContainer();
  const [closeEvent] = useAtom(flyoutCloseEventAtom);

  const gridKey = "credit-authorizations-grid";
  const flyoutKey = "authorized-credit-report-result-flyout";

  const getPredicate = (parentEntityTypeFilter?: string, parentEntityIdFilter?: string) => {
    if (!parentEntityTypeFilter && !parentEntityIdFilter) {
      return undefined;
    }

    let predicate: Predicate | undefined;

    if (parentEntityTypeFilter) {
      predicate = new Predicate("parentEntityType", "equal", parentEntityTypeFilter);
    }

    if (parentEntityIdFilter) {
      const parentEntityIdPredicate = new Predicate(
        "parentEntityId",
        "equal",
        `'${parentEntityIdFilter}'`
      );
      if (predicate) {
        predicate = predicate.and(parentEntityIdPredicate);
      } else {
        predicate = parentEntityIdPredicate;
      }
    }

    return predicate;
  };

  // Basic columns configuration - using type assertion for now
  const columnsConfiguration: GridColumn[] = [
    new GridColumn({
      field: "entityType",
      headerText: "Entity Type",
      type: "string",
    }),
    new GridColumn({
      field: "authorizedPersonName",
      headerText: "Authorized Person",
      type: "string",
    }),
    new GridColumn({
      field: "authorizedPersonEmail",
      headerText: "Email",
      type: "string",
    }),
    new GridColumn({
      field: "authorizedAt",
      headerText: "Authorized At",
      type: "date",
    }),
    new GridColumn({
      field: "decision_AsString",
      headerText: "Decision",
      type: "string",
    }),
  ];

  const handleRecordClick = (args: GridRecordClickEventArgs<CreditAuthorizationODataDTO>) => {
    navigateToFlyout(flyoutKey, {
      creditAuthorizationId: args.rowData.id,
      financialApplicationId: financialApplicationId ?? "",
      isApplicantLinked: isApplicantLinked.toString(),
    });
  };

  useEffect(() => {
    if (closeEvent && closeEvent.key === flyoutKey) {
      gridRef.current?.refresh();
    }
  }, [closeEvent]);

  // Use the useGrid hook with proper typing
  const { ready, gridRef, gridSettings } = useGrid<CreditAuthorizationODataDTO>({
    gridKey: gridKey,
    columnsConfiguration: columnsConfiguration,
    dataSource: `${variables.apiBaseUrl}/odata/creditauthorizationsodata`,
    getAuthRequestHeader: getAuthRequestHeader,
    predicate: getPredicate(parentEntityType, parentEntityId),
    gridConfiguration: {
      onRecordClick: handleRecordClick,
    },
  });

  return (
    <div className="credit-authorizations-grid">
      <SecondaryGrid ready={ready} gridRef={gridRef} gridSettings={gridSettings} />
    </div>
  );
};
